import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  Trash2,
  Edit2,
  Building2,
  AlertTriangle,
  Search,
} from 'lucide-react';

export default function CostCenters() {
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCenter, setDeletingCenter] = useState(null);
  const [forceDelete, setForceDelete] = useState(false);

  useEffect(() => {
    fetchCostCenters();
  }, []);

  const fetchCostCenters = async () => {
    setLoading(true);
    try {
      const res = await api.get('/cost-centers');
      setCostCenters(res.data.cost_centers || []);
    } catch (error) {
      console.error('Failed to fetch cost centers:', error);
      toast.error('Failed to load divisions');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (center = null) => {
    if (center) {
      setEditingCenter(center);
      setFormData({
        name: center.name,
        description: center.description || ''
      });
    } else {
      setEditingCenter(null);
      setFormData({ name: '', description: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingCenter) {
        await api.put(`/cost-centers/${editingCenter.id}`, formData);
        toast.success('Cost center updated');
      } else {
        await api.post('/cost-centers', formData);
        toast.success('Cost center created');
      }
      setDialogOpen(false);
      fetchCostCenters();
    } catch (error) {
      console.error('Failed to save cost center:', error);
      toast.error(error.response?.data?.detail || 'Failed to save division');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (center) => {
    setDeletingCenter(center);
    setForceDelete(false);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingCenter) return;

    try {
      await api.delete(`/cost-centers/${deletingCenter.id}?force=${forceDelete}`);
      toast.success('Cost center deleted');
      setDeleteDialogOpen(false);
      setDeletingCenter(null);
      fetchCostCenters();
    } catch (error) {
      console.error('Failed to delete cost center:', error);
      const detail = error.response?.data?.detail || 'Failed to delete division';
      
      // If it's in use, show the force delete option
      if (detail.includes('in use')) {
        toast.error(detail);
        setForceDelete(true);
      } else {
        toast.error(detail);
      }
    }
  };

  // Filter cost centers
  const filteredCenters = costCenters.filter(center =>
    center.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (center.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Divisions
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage divisions for jobs and employees
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-cost-center-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Division
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search divisions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-cost-centers"
        />
      </div>

      {/* Divisions List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {loading ? 'Loading...' : `${filteredCenters.length} Divisions`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredCenters.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? 'No divisions match your search' : 'No divisions yet. Add one to get started.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCenters.map((center) => (
                <div
                  key={center.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  data-testid={`cost-center-${center.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{center.name}</span>
                      {center.is_legacy && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                          Legacy
                        </span>
                      )}
                    </div>
                    {center.description && (
                      <p className="text-sm text-muted-foreground truncate">
                        {center.description}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Used in {center.usage_count} {center.usage_count === 1 ? 'place' : 'places'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(center)}
                      data-testid={`edit-${center.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClick(center)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-${center.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCenter ? 'Edit Division' : 'Add Division'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Operations, Marketing"
                required
                data-testid="cost-center-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                data-testid="cost-center-description-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-cost-center-btn">
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {deletingCenter?.usage_count > 0 && (
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
              )}
              Delete Division
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deletingCenter?.usage_count > 0 ? (
                <>
                  <p className="mb-2">
                    <strong>{deletingCenter.name}</strong> is currently used in{' '}
                    <strong>{deletingCenter.usage_count}</strong> {deletingCenter.usage_count === 1 ? 'place' : 'places'}.
                  </p>
                  {forceDelete ? (
                    <p className="text-red-600">
                      Are you sure you want to delete this division and remove it from all jobs and employees?
                      This action cannot be undone.
                    </p>
                  ) : (
                    <p>
                      Click "Delete Anyway" to remove this division from all associated jobs and employees.
                    </p>
                  )}
                </>
              ) : (
                <p>
                  Are you sure you want to delete <strong>{deletingCenter?.name}</strong>?
                  This action cannot be undone.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setForceDelete(false)}>
              Cancel
            </AlertDialogCancel>
            {deletingCenter?.usage_count > 0 && !forceDelete ? (
              <Button
                variant="destructive"
                onClick={() => setForceDelete(true)}
              >
                Delete Anyway
              </Button>
            ) : (
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                {forceDelete ? 'Yes, Delete' : 'Delete'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
